import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  Clock,
  PackageCheck,
  Timer,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useOrderHeader, type OrderFilters } from './orders.api';
import type { OrderHeader } from './types';

/**
 * The order pipeline, as five drillable counters.
 *
 * Each card is a status; each of its three sub-counts is that status narrowed
 * to past / today / future. Clicking any of them applies the corresponding
 * filters — which is what the original did too, but through twenty near-
 * identical hand-written blocks of JSX, one per clickable number.
 */

interface Bucket {
  key: string;
  icon: LucideIcon;
  labelKey: string;
  status: string;
  accent: string;
  total: (h: OrderHeader) => number;
  past?: (h: OrderHeader) => number;
  today?: (h: OrderHeader) => number;
  future?: (h: OrderHeader) => number;
}

const BUCKETS: Bucket[] = [
  {
    key: 'pending',
    icon: Clock,
    labelKey: 'orders.stats.pending',
    status: 'PLACED',
    accent: 'bg-rose-500/12 text-rose-600 dark:text-rose-400',
    total: (h) => h.pendingOrders,
    past: (h) => h.pendingPastOrders,
    today: (h) => h.pendingCurrentOrders,
    future: (h) => h.pendingFutureOrders,
  },
  {
    key: 'active',
    icon: Timer,
    labelKey: 'orders.stats.processing',
    status: 'INPROCESS',
    accent: 'bg-amber-500/12 text-amber-600 dark:text-amber-400',
    total: (h) => h.activeOrders,
    past: (h) => h.activePastOrders,
    today: (h) => h.activeCurrentOrders,
    future: (h) => h.activeFutureOrders,
  },
  {
    key: 'waiting',
    icon: PackageCheck,
    labelKey: 'orders.stats.waiting',
    status: 'WAITING',
    accent: 'bg-sky-500/12 text-sky-600 dark:text-sky-400',
    total: (h) => h.waitingOrders,
    past: (h) => h.waitingPastOrders,
    today: (h) => h.waitingCurrentOrders,
    future: (h) => h.waitingFutureOrders,
  },
  {
    key: 'inTransit',
    icon: Truck,
    labelKey: 'orders.stats.inTransit',
    status: 'INTRANSIT',
    accent: 'bg-violet-500/12 text-violet-600 dark:text-violet-400',
    total: (h) => h.inTransitOrders,
    past: (h) => h.inTransitPastOrders,
    today: (h) => h.inTransitCurrentOrders,
    future: (h) => h.inTransitFutureOrders,
  },
  {
    key: 'completed',
    icon: CheckCircle2,
    labelKey: 'orders.stats.completed',
    status: 'DISPATCHED',
    accent: 'bg-emerald-500/12 text-emerald-600 dark:text-emerald-400',
    total: (h) => h.completedOrders,
  },
];

const SPANS = [
  { tag: 'PAST', labelKey: 'orders.filters.past' },
  { tag: 'TODAY', labelKey: 'orders.filters.today' },
  { tag: 'FUTURE', labelKey: 'orders.filters.future' },
] as const;

export function OrderStats({
  filters,
  onDrill,
}: {
  filters: OrderFilters;
  onDrill: (status: string, time: string) => void;
}) {
  const { t } = useTranslation();
  const { data, isPending, isFetching, isError } = useOrderHeader(filters);
  // Counts on screen being refreshed — after an order action invalidates them.
  const busy = isFetching && !isPending;

  if (isError) return null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      {BUCKETS.map((bucket) => {
        const active = filters.status === bucket.status;
        const spans = [bucket.past, bucket.today, bucket.future];
        const hasBreakdown = spans.some(Boolean);

        return (
          <div
            key={bucket.key}
            className={cn(
              'bg-card relative overflow-hidden rounded-xl border transition-colors',
              active && 'border-primary/40 ring-primary/15 ring-2'
            )}
          >
            <BusyOverlay show={busy} size="sm" />
            <div className={cn(busy && 'opacity-60 transition-opacity')}>
              <button
                type="button"
                onClick={() => onDrill(bucket.status, '')}
                className="hover:bg-accent/50 flex w-full items-center gap-3 p-3 text-left transition-colors"
              >
                <span
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-lg',
                    bucket.accent
                  )}
                >
                  <bucket.icon className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-muted-foreground truncate text-xs font-medium">
                    {t(bucket.labelKey)}
                  </p>
                  {isPending ? (
                    <Skeleton className="mt-1 h-7 w-14" />
                  ) : (
                    <p className="text-2xl leading-tight font-semibold tabular-nums">
                      {formatNumber(data ? bucket.total(data) : 0)}
                    </p>
                  )}
                </div>
              </button>

              {hasBreakdown && (
                <div className="grid grid-cols-3 border-t">
                  {SPANS.map((span, index) => {
                    const read = spans[index];
                    const value = data && read ? read(data) : 0;
                    const selected =
                      filters.status === bucket.status &&
                      filters.time === span.tag;

                    return (
                      <button
                        key={span.tag}
                        type="button"
                        onClick={() => onDrill(bucket.status, span.tag)}
                        className={cn(
                          'hover:bg-accent/50 border-r px-2 py-1.5 text-center transition-colors last:border-r-0',
                          selected && 'bg-accent'
                        )}
                      >
                        {isPending ? (
                          <Skeleton className="mx-auto h-4 w-8" />
                        ) : (
                          <span className="block text-sm font-medium tabular-nums">
                            {formatNumber(value)}
                          </span>
                        )}
                        <span className="text-muted-foreground block text-[0.65rem]">
                          {t(span.labelKey)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
